/**
 * @flow
 */

import React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { compose, branch, renderComponent, withProps } from 'recompose';
import { FormattedMessage } from 'react-intl';
import { isEmpty, reduce, get, sortBy, sumBy } from 'lodash';
import { Loader, Grid } from 'semantic-ui-react';

import { Table } from '../../../../components';
import SellerReport from '../../containers/seller/SellerReport';
import SellerReportSummary from '../../containers/seller/SellerReportSummary';

const SellerReportCell = styled(Table.Cell)`
    padding: 0px !important;
`;

const EditInvoiceHeaderCell = styled(Table.HeaderCell)`
    width: 62px;
`;

const EmptyMessage = ({ columns }) => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={columns} textAlign="center">
            <FormattedMessage
                id="pages.report.ReportTable.noReports"
                defaultMessage="There is no reports today"
            />
        </Table.Cell>
    </Table.Row>
);

const ReportTable = ({
    loading,
    sellers,
    displayEditButton,
    sellerReportCellColSpan,
    hasReports,
}) => (
    <Table celled fixed>
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.report.ReportTable.date"
                        defaultMessage="Date"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.report.ReportTable.client"
                        defaultMessage="Client"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.report.ReportTable.service"
                        defaultMessage="Service/Product"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="center">
                    <FormattedMessage
                        id="pages.report.ReportTable.quantity"
                        defaultMessage="Quantity"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="right">
                    <FormattedMessage
                        id="pages.report.ReportTable.price"
                        defaultMessage="Price"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="right">
                    <FormattedMessage
                        id="pages.report.ReportTable.total"
                        defaultMessage="Total"
                    />
                </Table.HeaderCell>
                {displayEditButton && <EditInvoiceHeaderCell />}
            </Table.Row>
        </Table.Header>
        <Table.Body>
            {hasReports ? (
                sellers.map(
                    seller =>
                        !isEmpty(seller.report.invoices) && (
                            <Table.Row key={seller.id}>
                                <SellerReportCell
                                    colSpan={sellerReportCellColSpan}
                                >
                                    <SellerReport
                                        seller={seller}
                                        displayEditButton={displayEditButton}
                                    />
                                </SellerReportCell>
                            </Table.Row>
                        ),
                )
            ) : (
                <EmptyMessage columns={sellerReportCellColSpan} />
            )}
        </Table.Body>
    </Table>
);

const SellerReportTable = ({
    loading,
    sellers,
    displayEditButton,
    sellerReportCellColSpan,
    hasReports,
}) => (
    <Grid columns={1} padded>
        <Grid.Row>
            <Grid.Column>
                <SellerReportSummary loading={loading} sellers={sellers} />
            </Grid.Column>
        </Grid.Row>
        <Grid.Row>
            <Grid.Column>
                <ReportTable
                    loading={loading}
                    sellers={sellers}
                    displayEditButton={displayEditButton}
                    sellerReportCellColSpan={sellerReportCellColSpan}
                    hasReports={hasReports}
                />
            </Grid.Column>
        </Grid.Row>
    </Grid>
);

const props = withProps(({ isOwner, masters, cabinets }) => ({
    displayEditButton: isOwner,
    sellerReportCellColSpan: isOwner ? 7 : 6,
    hasReports: !!reduce(
        [...masters, ...cabinets],
        (sum, seller) => sum + get(seller, 'report.invoices', []).length,
        0,
    ),
    sellers: sortBy(
        [...masters, ...cabinets],
        seller =>
            0 -
            reduce(
                get(seller, 'report.invoices', []),
                (result, invoice) =>
                    result +
                    sumBy(
                        get(invoice, 'items', []),
                        ({ price = 0, quantity = 0 }) => price * quantity,
                    ),
                0,
            ),
    ),
}));

const mapStateToProps = ({ user }) => ({
    isOwner: user.get('isOwner'),
});

// Show spinner while report loading
const withSpinner = branch(
    props => props.loading,
    renderComponent(() => <Loader size="big" inline="centered" active />),
);

export default compose(
    connect(mapStateToProps),
    props,
    withSpinner,
)(SellerReportTable);
